import * as React from 'react';
import * as L from 'leaflet';
import styled from 'styled-components';

import { MapContainer, Marker, ScaleControl } from 'react-leaflet';
import { Button } from '@independent-software/typeui/controls/Button';
import { Label } from '@independent-software/typeui/controls/Label';
import { Latitude, Longitude } from '@independent-software/typeui/formatters/GIS';
import { MarkerIcon } from './svg-icons/MarkerIcon';
import { StandardLayerControl } from './StandardLayerControl';

interface IProps {
  className?: string;
  // Location initially shown op map. Can be null.
  lat?: number;
  lng?: number;
  // Initial zoom level.
  zoom: number;
  // Handler when a location is selected.         
  onSelect: (lat: number, lng: number) => void;
}

interface IState {
  // New location selected:
  location: L.LatLngLiteral;
}

//
// PickLocationMap shows map with a single maker, or no marker if no location was
// specified. If the map is clicked, a secondary marker with a new location appears.
// This new location is sent to the parent when the "confirm" button is clicked.
// 
class PickLocationMapBase extends React.Component<IProps, IState> {
  private map: L.Map;

  constructor(props: IProps) {
    super(props);

    this.state = {
      location: null
    }
  }

  // Register/unregister 'click' event listener:
  handleMapCreated = (map: L.Map) => {
    this.map = map;
    this.map.addEventListener('click', (e: L.LeafletMouseEvent) => {
      this.handleClick(e.latlng);
    });
  }
  componentWillUnmount = () => {
    this.map.removeEventListener('click');
  }

  // If lat/lng changed, then pan map.
  componentDidUpdate = (newprops: IProps) => {
    if(newprops.lat != this.props.lat || newprops.lng != this.props.lng) {
      if(this.map && this.getLocation() != null) this.map.panTo(this.getLocation());
    }
  }

  // If map is clicked, set a clicked location marker.
  handleClick = (latlng: L.LatLng) => {
    this.setState({
      location: { lat: latlng.lat, lng: latlng.lng }
    });
  }

  // If confirm button is clicked, notify subscriber.
  handleConfirm = () => {
    this.props.onSelect(this.state.location.lat, this.state.location.lng);
    this.setState({ location: null });
  }

  // Converts current prop location to LatLngLiteral. Returns null
  // if current location is invalid.
  getLocation = (): L.LatLngLiteral => {
    if(isNaN(parseFloat(this.props.lat as any)) || isNaN(parseFloat(this.props.lng as any))) {
       return null;
    }
    return { lat: this.props.lat, lng: this.props. lng };
  }

  render() {
    let p = this.props;
    let location = this.getLocation();

    return (
      <div className={p.className}>

        <MapContainer 
          whenCreated={this.handleMapCreated} 
          style={{ height: '100%', width: '100%' }} 
          center={location ?? [0,0]}  /* center is 0,0 if initial location not set */
          zoom={p.zoom} 
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <StandardLayerControl/>
          <ScaleControl position="topleft" imperial={false}/>
          {/* Show marker for current location, if any */}
          {location && 
            <Marker opacity={this.state.location ? 0.5 : 1} position={location} icon={MarkerIcon(25, 'white')}/>}
          {/* Show marker for clicked location, if any */}
          {this.state.location && 
            <Marker position={this.state.location} icon={MarkerIcon(25, 'white')}/>}
        </MapContainer>

        <ButtonHolder>
          {this.state.location == null && location == null && <Label basic>Click map to select location</Label>}
          {this.state.location == null && location != null && <Label basic>Click map to change location</Label>}
          {this.state.location != null && <Button secondary onClick={this.handleConfirm}>
            Confirm
            <Label attached="left" basic><Latitude value={this.state.location.lat}/></Label>
            <Label attached="left" basic pointing="right"><Longitude value={this.state.location.lng}/></Label>
          </Button>}
        </ButtonHolder>

      </div>
    );
  }
}

const ButtonHolder = styled('div')`
  position: absolute;
  z-index:  9999;
  bottom:   35px;
  right:    10px;
`

const PickLocationMap = styled(PickLocationMapBase)`
  position: relative;
  width:    100%;
  height:   100%;
`

export { PickLocationMap }
